import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useGraphStore } from "./graph";
import { useQuestionsStore } from "./questions";

export type HistoryEntry = {
	type: "single" | "autofill";
	taskId: string;
	changes: { attrId: string; previousValue: any }[];
};

export const useQuestionHistoryStore = defineStore("QuestionHistoryStore", () => {
	const historyStack = ref<HistoryEntry[]>([]);
	const canUndo = computed(() => historyStack.value.length > 0);

	function push(entry: HistoryEntry) {
		historyStack.value.push(entry);
	}

	function clear() {
		historyStack.value = [];
	}

	async function undo() {
		if (historyStack.value.length === 0) return;
		const entry = historyStack.value.pop()!;

		const graphStore = useGraphStore();
		const questionsStore = useQuestionsStore();

		const promises: Promise<any>[] = [];
		const attributesToMark: { instanceAttrId: string; value: any; source: string }[] = [];

		for (const change of entry.changes) {
			promises.push(graphStore.instance.attribute.update(change.attrId, change.previousValue));
			attributesToMark.push({
				instanceAttrId: change.attrId,
				value: change.previousValue,
				source: "web_ui_undo",
			});
		}

		const instanceTask = graphStore.instance.task.byId(entry.taskId);
		if (instanceTask && attributesToMark.length > 0) {
			promises.push(graphStore.instance.task.markAnsweredBy(instanceTask, attributesToMark));
		}

		if (entry.type === "autofill") {
			questionsStore.hideFromOpenQuestions = questionsStore.hideFromOpenQuestions.filter(
				(id) => id !== entry.taskId,
			);
		}

		await Promise.all(promises);
	}

	return {
		historyStack,
		canUndo,
		push,
		clear,
		undo,
	};
});
